import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  booleanAttribute,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { PrefixUrlPipe } from '../../pipes/prefix-url.pipe';
import { prefixUrl } from '../../utils/url.util';

export type EventqSiteOverviewMapMode = 'property' | 'google';

export interface EventqSiteMapImage {
  id: string;
  src: string;
  label: string;
}

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const GOOGLE_MAP_ZOOM = 18;
const GOOGLE_MAP_SCRIPT_SELECTOR = 'script[data-eventq-google-maps]';
const GOOGLE_MAP_CALLBACK = '__eventqGoogleMapsReady';
/** Runtime `google.maps.ControlPosition` values. TOP_LEFT = 1, RIGHT_BOTTOM = 9. */
const GOOGLE_CONTROL_TOP_LEFT = 1;
const GOOGLE_CONTROL_RIGHT_BOTTOM = 9;
/**
 * Runtime `google.maps.MapTypeControlStyle.HORIZONTAL_BAR`.
 * Current `@types/google.maps` lists this as 0; the Maps script still uses 1.
 */
const GOOGLE_MAP_TYPE_HORIZONTAL_BAR = 1;
const LOCATION_MAP_UNAVAILABLE = 'location map not available';

type GoogleMapsWindow = Window & {
  google?: {
    maps?: {
      importLibrary?: unknown;
      event?: { trigger?: (instance: object, eventName: string) => void };
    };
  };
  [GOOGLE_MAP_CALLBACK]?: () => void;
};

let googleMapsApiLoad: Promise<void> | null = null;

const CONTROL_ICON = {
  prev: '/assets/workspace/overview/maps/chevron-left.svg',
  next: '/assets/workspace/overview/maps/chevron-right.svg',
  zoomIn: '/assets/workspace/overview/maps/zoom-in.svg',
  zoomOut: '/assets/workspace/overview/maps/zoom-out.svg',
  reset: '/assets/workspace/overview/maps/reset.svg',
} as const;

@Component({
  selector: 'eventq-site-overview-maps',
  imports: [GoogleMap, MapMarker, PrefixUrlPipe],
  templateUrl: './site-overview-maps.html',
  styleUrl: './site-overview-maps.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'eventq-site-overview-maps-host',
    '[attr.aria-busy]': 'loading() || googleMapLoading() ? "true" : null',
  },
})
export class EventqSiteOverviewMaps {
  private readonly document = inject(DOCUMENT);

  readonly propertyMaps = input.required<readonly EventqSiteMapImage[]>();
  readonly googleMapImageSrc = input('');
  readonly propertyMapIconSrc = input.required<string>();
  readonly googleMapIconSrc = input.required<string>();
  readonly emptyMessage = input('No maps available');
  readonly loading = input(false, { transform: booleanAttribute });
  /** Host-provided Maps JavaScript API key (never baked into the library). */
  readonly googleMapsApiKey = input('');
  readonly latitude = input<number | null, unknown>(null, { transform: nullableNumber });
  readonly longitude = input<number | null, unknown>(null, { transform: nullableNumber });

  readonly controlIcons = CONTROL_ICON;

  private readonly viewportRef = viewChild<ElementRef<HTMLElement>>('viewport');

  private readonly mode = signal<EventqSiteOverviewMapMode>('property');
  private readonly propertyIndex = signal(0);
  private readonly viewportHovered = signal(false);
  private readonly scale = signal(ZOOM_MIN);
  private readonly translateX = signal(0);
  private readonly translateY = signal(0);
  private readonly isPanning = signal(false);
  readonly googleApiState = signal<'idle' | 'loading' | 'ready' | 'error'>('idle');
  private readonly googleMapReady = signal(false);
  private googleMap: google.maps.Map | null = null;

  private panPointerId: number | null = null;
  private panOriginX = 0;
  private panOriginY = 0;
  private panStartTranslateX = 0;
  private panStartTranslateY = 0;

  readonly activeMode = this.mode.asReadonly();

  readonly hasCoordinates = computed(() => {
    const lat = this.latitude();
    const lng = this.longitude();
    return lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);
  });

  readonly hasApiKey = computed(() => this.googleMapsApiKey().trim().length > 0);

  readonly canUseLiveGoogleMap = computed(() => this.hasApiKey() && this.hasCoordinates());

  readonly hasContent = computed(
    () => this.propertyMaps().length > 0 || this.canUseLiveGoogleMap(),
  );

  readonly activeProperty = computed(() => {
    const maps = this.propertyMaps();
    if (maps.length === 0) {
      return null;
    }
    const index = ((this.propertyIndex() % maps.length) + maps.length) % maps.length;
    return maps[index] ?? null;
  });

  readonly showInteractiveGoogleMap = computed(
    () =>
      this.mode() === 'google' &&
      this.canUseLiveGoogleMap() &&
      this.googleApiState() !== 'error',
  );

  readonly googleUnavailableMessage = computed(() => {
    if (this.mode() !== 'google') {
      return null;
    }
    if (!this.canUseLiveGoogleMap() || this.googleApiState() === 'error') {
      return LOCATION_MAP_UNAVAILABLE;
    }
    return null;
  });

  readonly googleMapCenter = computed<google.maps.LatLngLiteral>(() => ({
    lat: this.latitude() as number,
    lng: this.longitude() as number,
  }));

  readonly googleMapOptions: google.maps.MapOptions = {
    mapTypeId: 'roadmap' as google.maps.MapTypeId,
    fullscreenControl: false,
    mapTypeControl: true,
    streetViewControl: true,
    cameraControl: true,
    zoomControl: false,
    mapTypeControlOptions: {
      mapTypeIds: ['roadmap', 'satellite'],
      position: GOOGLE_CONTROL_TOP_LEFT as google.maps.ControlPosition,
      style: GOOGLE_MAP_TYPE_HORIZONTAL_BAR as google.maps.MapTypeControlStyle,
    },
    cameraControlOptions: {
      position: GOOGLE_CONTROL_RIGHT_BOTTOM as google.maps.ControlPosition,
    },
    streetViewControlOptions: {
      position: GOOGLE_CONTROL_RIGHT_BOTTOM as google.maps.ControlPosition,
    },
  };

  readonly activeImageSrc = computed(() => {
    if (this.mode() === 'google') {
      return '';
    }
    return this.activeProperty()?.src ?? '';
  });

  readonly showThumbnails = computed(
    () => this.mode() === 'property' && this.propertyMaps().length > 0,
  );

  readonly showViewportControls = computed(
    () =>
      this.mode() === 'property' &&
      this.viewportHovered() &&
      this.propertyMaps().length > 0 &&
      !this.loading(),
  );

  readonly showNav = computed(
    () => this.showViewportControls() && this.propertyMaps().length >= 2,
  );

  readonly canZoomIn = computed(() => this.scale() < ZOOM_MAX);
  readonly canZoomOut = computed(() => this.scale() > ZOOM_MIN);

  readonly imageTransform = computed(
    () =>
      `translate(${this.translateX()}px, ${this.translateY()}px) scale(${this.scale()})`,
  );

  readonly isPropertyInteractive = computed(
    () => this.mode() === 'property' && this.propertyMaps().length > 0,
  );

  readonly viewportPanning = this.isPanning.asReadonly();

  readonly googleMapLoading = computed(
    () =>
      this.showInteractiveGoogleMap() &&
      (this.googleApiState() !== 'ready' || !this.googleMapReady()),
  );

  iconMask(src: string): string {
    return `url("${prefixUrl(src)}")`;
  }

  selectMode(mode: EventqSiteOverviewMapMode): void {
    this.mode.set(mode);
    this.propertyIndex.set(0);
    this.resetView();
    if (mode !== 'property') {
      this.viewportHovered.set(false);
    }
    this.googleMap = null;
    this.googleMapReady.set(false);
    if (mode === 'google' && this.canUseLiveGoogleMap()) {
      this.loadGoogleMapsApi();
    }
  }

  selectPropertyIndex(index: number): void {
    const maps = this.propertyMaps();
    if (maps.length === 0 || index < 0 || index >= maps.length) {
      return;
    }
    this.propertyIndex.set(index);
    this.resetView();
  }

  isThumbnailActive(index: number): boolean {
    return this.propertyIndex() === index;
  }

  onViewportPointerEnter(): void {
    if (this.mode() === 'property') {
      this.viewportHovered.set(true);
    }
  }

  onViewportPointerLeave(): void {
    this.viewportHovered.set(false);
    this.endPan();
  }

  goPrev(): void {
    const maps = this.propertyMaps();
    if (maps.length < 2) {
      return;
    }
    const nextIndex = (this.propertyIndex() - 1 + maps.length) % maps.length;
    this.propertyIndex.set(nextIndex);
    this.resetView();
  }

  goNext(): void {
    const maps = this.propertyMaps();
    if (maps.length < 2) {
      return;
    }
    const nextIndex = (this.propertyIndex() + 1) % maps.length;
    this.propertyIndex.set(nextIndex);
    this.resetView();
  }

  zoomIn(): void {
    if (!this.canZoomIn()) {
      return;
    }
    this.scale.update((value) => Math.min(ZOOM_MAX, +(value + ZOOM_STEP).toFixed(2)));
    this.clampPan();
  }

  zoomOut(): void {
    if (!this.canZoomOut()) {
      return;
    }
    this.scale.update((value) => Math.max(ZOOM_MIN, +(value - ZOOM_STEP).toFixed(2)));
    this.clampPan();
  }

  resetView(): void {
    this.scale.set(ZOOM_MIN);
    this.translateX.set(0);
    this.translateY.set(0);
  }

  onGoogleMapInitialized(map: google.maps.Map): void {
    this.googleMap = map;
    this.googleMapReady.set(true);
    this.resizeGoogleMap(map);
    // Init can run before the viewport has its min-height. Resize again after layout.
    this.document.defaultView?.requestAnimationFrame(() => this.resizeGoogleMap(map));
  }

  onGoogleMapAuthFailure(): void {
    this.googleMap = null;
    this.googleApiState.set('error');
    this.googleMapReady.set(false);
  }

  private resizeGoogleMap(map: google.maps.Map): void {
    if (this.googleMap !== map) {
      return;
    }
    const mapsWindow = this.document.defaultView as GoogleMapsWindow | null;
    mapsWindow?.google?.maps?.event?.trigger?.(map, 'resize');
  }

  centerGoogleMap(): void {
    if (!this.googleMap || !this.hasCoordinates()) {
      return;
    }
    this.googleMap.setCenter(this.googleMapCenter());
    this.googleMap.setZoom(GOOGLE_MAP_ZOOM);
  }

  private loadGoogleMapsApi(): void {
    const mapsWindow = this.document.defaultView as GoogleMapsWindow | null;
    // `google.maps` appears before the loader finishes; `importLibrary` marks a usable API.
    if (typeof mapsWindow?.google?.maps?.importLibrary === 'function') {
      this.googleApiState.set('ready');
      return;
    }

    this.googleApiState.set('loading');
    googleMapsApiLoad ??= this.createGoogleMapsScript();
    void googleMapsApiLoad.then(
      () => this.googleApiState.set('ready'),
      () => {
        googleMapsApiLoad = null;
        this.googleApiState.set('error');
      },
    );
  }

  private createGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const existing = this.document.querySelector<HTMLScriptElement>(
        GOOGLE_MAP_SCRIPT_SELECTOR,
      );
      const script = existing ?? this.document.createElement('script');
      const mapsWindow = this.document.defaultView as GoogleMapsWindow | null;

      const onError = (): void => {
        cleanup();
        script.remove();
        reject(new Error('Google Maps JavaScript API failed to load'));
      };
      const cleanup = (): void => {
        script.removeEventListener('error', onError);
        if (mapsWindow) {
          delete mapsWindow[GOOGLE_MAP_CALLBACK];
        }
      };

      script.addEventListener('error', onError, { once: true });

      // The loader calls this once the API is usable; the script `load` event is too early.
      if (mapsWindow) {
        mapsWindow[GOOGLE_MAP_CALLBACK] = () => {
          cleanup();
          resolve();
        };
      }

      if (!existing) {
        const params = new URLSearchParams({
          key: this.googleMapsApiKey().trim(),
          v: 'weekly',
          loading: 'async',
          callback: GOOGLE_MAP_CALLBACK,
        });
        script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
        script.async = true;
        script.dataset['eventqGoogleMaps'] = '';
        this.document.head.append(script);
      }
    });
  }

  onImagePointerDown(event: PointerEvent): void {
    if (this.mode() !== 'property' || event.button !== 0) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest('button')) {
      return;
    }

    this.isPanning.set(true);
    this.panPointerId = event.pointerId;
    this.panOriginX = event.clientX;
    this.panOriginY = event.clientY;
    this.panStartTranslateX = this.translateX();
    this.panStartTranslateY = this.translateY();
    (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  onImagePointerMove(event: PointerEvent): void {
    if (!this.isPanning() || event.pointerId !== this.panPointerId) {
      return;
    }
    const dx = event.clientX - this.panOriginX;
    const dy = event.clientY - this.panOriginY;
    this.translateX.set(this.panStartTranslateX + dx);
    this.translateY.set(this.panStartTranslateY + dy);
    this.clampPan();
  }

  onImagePointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.panPointerId) {
      return;
    }
    this.endPan(event);
  }

  private endPan(event?: PointerEvent): void {
    if (this.panPointerId !== null && event) {
      (event.currentTarget as HTMLElement | null)?.releasePointerCapture?.(this.panPointerId);
    }
    this.isPanning.set(false);
    this.panPointerId = null;
  }

  private clampPan(): void {
    const scale = this.scale();
    if (scale <= ZOOM_MIN) {
      this.translateX.set(0);
      this.translateY.set(0);
      return;
    }

    const viewport = this.viewportRef()?.nativeElement;
    const width = viewport?.clientWidth ?? 0;
    const height = viewport?.clientHeight ?? 0;
    const maxX = (width * (scale - 1)) / 2;
    const maxY = (height * (scale - 1)) / 2;

    this.translateX.update((x) => Math.max(-maxX, Math.min(maxX, x)));
    this.translateY.update((y) => Math.max(-maxY, Math.min(maxY, y)));
  }
}

function nullableNumber(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}
